import { RouterProvider, createRouter } from '@tanstack/react-router';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './modules/i18n/config';
import './index.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/auth-context';
import { AnalyticsProviderWrapper } from './context/analytics-context';
import { SkeletonThemeProvider } from './components/ui/skeleton-theme-provider';
import { AIUpgradeModal } from './components/shared/AIUpgradeModal';
import { routeTree } from './routeTree.gen';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (previously cacheTime)
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

// Create a new router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  context: {
    // auth will initially be undefined
    // We'll be passing down the auth state from within a React component
    auth: undefined!,
  },
});

function InnerApp() {
  const auth = useAuth();
  return (
    <AnalyticsProviderWrapper>
      <SkeletonThemeProvider>
        <RouterProvider router={router} context={{ auth }} />
        <Toaster richColors position='top-center' />
      </SkeletonThemeProvider>
    </AnalyticsProviderWrapper>
  );
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
        <AIUpgradeModal />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>
  );
}
